/**
 * WhatsApp Number Formatter for UAE (+971)
 * 
 * Formats and normalizes UAE WhatsApp numbers:
 * - Default country code: +971
 * - Auto-formats 9-digit numbers starting with 5 to +971 5X XXX XXXX
 * - Normalizes numbers with country code (9715XXXXXXXX or +9715XXXXXXXX)
 * - Preserves spacing in UI while storing in consistent format
 */

const UAE_COUNTRY_CODE = '+971';
const UAE_MOBILE_PREFIX = '5';

/**
 * Removes all formatting characters (spaces, dashes, parentheses)
 * @param {string} value - The phone number string
 * @returns {string} - Cleaned phone number
 */
const removeFormatting = (value) => {
  return value.replace(/[\s\-\(\)]/g, '');
};

/**
 * Checks if a number matches UAE mobile pattern (9 digits starting with 5)
 * @param {string} digits - The digits only (no formatting)
 * @returns {boolean}
 */
const isUAEMobile = (digits) => {
  // UAE mobile: 9 digits starting with 5
  return /^5\d{8}$/.test(digits);
};

/**
 * Formats a phone number for display with spacing
 * Format: +971 5X XXX XXXX
 * @param {string} value - The phone number
 * @returns {string} - Formatted phone number for display
 */
export const formatWhatsAppNumber = (value) => {
  if (!value) return '';

  // Remove all formatting first
  let cleaned = removeFormatting(value);

  // Remove leading + if present for processing
  const hadPlus = cleaned.startsWith('+');
  if (hadPlus) {
    cleaned = cleaned.substring(1);
  }

  let mobilePart = '';
  let shouldFormat = false;

  // Handle UAE country code (971)
  if (cleaned.startsWith('971')) {
    mobilePart = cleaned.substring(3); // Get digits after 971
    shouldFormat = true;
  } else if (isUAEMobile(cleaned)) {
    // If it's a 9-digit number starting with 5 (without country code), format it
    mobilePart = cleaned;
    shouldFormat = true;
  } else if (cleaned.startsWith('5') && cleaned.length <= 9) {
    // User is typing a UAE mobile number (not yet complete)
    // Return as is, but preserve + if it was there
    return hadPlus ? '+' + cleaned : cleaned;
  } else {
    // Doesn't match UAE pattern, return as is
    return hadPlus ? '+' + cleaned : cleaned;
  }

  // Format for display: +971 5X XXX XXXX
  if (shouldFormat && mobilePart.length === 9 && mobilePart.startsWith('5')) {
    return `+971 ${mobilePart.substring(0, 2)} ${mobilePart.substring(2, 5)} ${mobilePart.substring(5)}`;
  }

  // If mobile part is incomplete but matches pattern start, allow partial formatting
  if (shouldFormat && mobilePart.startsWith('5') && mobilePart.length > 0 && mobilePart.length < 9) {
    // Return with country code but don't format spacing yet (user still typing)
    return `+971 ${mobilePart}`;
  }

  // Return as is (might be incomplete or different format)
  return hadPlus ? '+' + cleaned : cleaned;
};

/**
 * Normalizes a phone number to consistent international format for storage
 * Format: +9715XXXXXXXX (no spaces)
 * @param {string} value - The phone number (may have formatting)
 * @returns {string} - Normalized phone number in format +9715XXXXXXXX
 */
export const normalizeWhatsAppNumber = (value) => {
  if (!value) return '';

  // Remove all formatting (spaces, dashes, etc.)
  let cleaned = removeFormatting(value);

  // If already in correct format (+971XXXXXXXXX), return as is
  if (cleaned.startsWith('+971') && cleaned.length === 13) {
    return cleaned;
  }

  // Remove + if present
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }

  // Handle UAE country code (971)
  if (cleaned.startsWith('971')) {
    cleaned = cleaned.substring(3); // Remove '971'
  }

  // If it's a 9-digit number starting with 5, add UAE country code
  if (isUAEMobile(cleaned)) {
    return UAE_COUNTRY_CODE + cleaned;
  }

  // If number doesn't match UAE mobile pattern, don't modify it
  // (let backend validation handle it)
  // But if it starts with 5 and is 9 digits, assume it's UAE mobile
  if (cleaned.startsWith('5') && cleaned.length === 9) {
    return UAE_COUNTRY_CODE + cleaned;
  }

  // Return as is if it doesn't match UAE pattern (might be valid for another country)
  // The backend will validate
  return cleaned;
};

/**
 * Handles onChange event for WhatsApp number input
 * Formats the value while typing and updates the field value
 * @param {Event} event - The onChange event
 * @param {Function} onChange - The field onChange handler from react-hook-form
 */
export const handleWhatsAppNumberChange = (event, onChange) => {
  const inputValue = event.target.value;
  
  // Allow backspace/delete by checking if user is removing characters
  // Format the value for display
  const formatted = formatWhatsAppNumber(inputValue);
  
  // Update the field with formatted value
  onChange(formatted);
};

